import { IconButton } from '@creditornot/cb-button';
import { ChainLink, CrossCircleFilled, Trashbin } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { LegacyPopover } from '@creditornot/cb-popover';
import { Spinner } from '@creditornot/cb-progress';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { PhotoShootImageData } from 'apis/mips/types';
import { Typography } from 'common/typography';

import { PhotoShootQueryKeys } from '../../constants/queryKeys';

export type ImageTileProps = {
  image: PhotoShootImageData;
  onDelete?: (imageId: string) => void;
  isDeleting?: boolean;
  isLinked?: boolean;
  isReadOnly?: boolean;
  isOverlay?: boolean;
  isDragging?: boolean;
};

const borderRadius = '8px';
const Root = styled.div<{ imgUrl?: string; isDragging?: boolean; isOverlay?: boolean; isReadOnly?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ isDragging }) => (isDragging ? 0.5 : 1)};
  border-radius: ${borderRadius};
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  background: ${({ imgUrl }) => `url(${imgUrl}) no-repeat center center`};
  background-color: ${cbSemanticCssVars.surfaceMain};
  background-size: contain;
  height: 100%;
  width: 100%;
  transition: transform ease-in 100ms;
  transform: scale(${({ isOverlay }) => (isOverlay ? 0.6 : 1)});
  cursor: ${({ isOverlay, isReadOnly }) => {
    switch (true) {
      case isReadOnly:
        return 'default';
      case isOverlay:
        return 'grabbing';
      default:
        return 'grab';
    }
  }};
`;

const Loader = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: ${cssThemedValue({ light: colors.pepper76, dark: colors.salt80 })};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const PhotoError = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin: 0 8px;
`;

const PhotoErrorMessage = styled.div`
  color: ${colors.strawberry100};
  display: flex;
  align-items: center;
  margin-block-end: 8px;
`;

const PhotoErrorName = styled.div`
  ${typographyCss.Body3()};
  text-align: center;
  overflow-wrap: anywhere; /* for long file names */
`;

const ErrorIcon = styled(CrossCircleFilled)`
  width: 16px;
  height: 16px;
  display: inline-block;
  margin-inline-end: 4px;
  fill: ${colors.strawberry100};
`;

const TopBar = styled.div`
  position: absolute;
  inset-block-start: -1px;
  inset-inline-start: -1px;
  inset-inline-end: -1px;
  border-radius: ${borderRadius} ${borderRadius} 0 0;
  padding: 8px;
  display: flex;
  gap: 4px;
  align-items: center;
  backdrop-filter: blur(10px);
  color: ${cssThemedValue({ light: colors.salt100, dark: colors.pepper100 })};
  background: ${cssThemedValue({ light: colors.pepper40, dark: colors.salt44 })};
  opacity: 0;
  transition: opacity ease-in 100ms;
  ${Root}:hover & {
    opacity: 1;
  }
`;

const DeleteIconButton = styled(IconButton).attrs({ size: 'tiny', variant: 'inverse' })`
  background-color: ${colors.strawberry100};
`;

const TopBarText = styled(Typography.Small)`
  flex: 1;
  overflow-wrap: anywhere; /* for long file names */
`;

const LinkIconContainer = styled.div`
  position: absolute;
  inset-block-end: 8px;
  inset-inline-start: 8px;
`;

const LinkIconBox = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${colors.lime100};
`;

const LinkIcon = styled(ChainLink)`
  position: relative;
  height: 12px;
  fill: ${cssThemedValue({ light: colors.salt100, dark: colors.pepper100 })};
`;

export const ImageTile = ({
  image,
  onDelete,
  isDeleting = false,
  isLinked = false,
  isReadOnly = false,
  isOverlay = false,
  isDragging = false,
}: ImageTileProps) => {
  const {
    data: loadedPhoto,
    isLoading: isPhotoLoading,
    error,
  } = useQuery(
    PhotoShootQueryKeys.LOAD_IMAGE(image.url),
    () => {
      return new Promise<HTMLImageElement>((resolve, reject) => {
        const imageElement = new Image();
        imageElement.onload = () => {
          resolve(imageElement);
        };
        imageElement.onerror = () => reject(`Cannot load image: ${image.url}`);
        imageElement.src = image.url;
      });
    },
    { retry: false, refetchOnWindowFocus: false },
  );

  const isLoading = isPhotoLoading || isDeleting;

  return (
    <Root isDragging={isDragging} isOverlay={isOverlay} isReadOnly={isReadOnly} imgUrl={loadedPhoto?.src}>
      {isLoading && (
        <Loader>
          <Spinner size="large" data-test-id="image-tile__spiner" />
        </Loader>
      )}

      {!isLoading && !isOverlay && (
        <>
          <TopBar>
            <TopBarText>{image.name}</TopBarText>
            {!isReadOnly && onDelete && (
              <DeleteIconButton onClick={() => onDelete(image.id)} data-test-id="image-tile__delete-button">
                <Trashbin />
              </DeleteIconButton>
            )}
          </TopBar>

          {isLinked && (
            <LinkIconContainer data-test-id="image-tile__link-icon">
              <LegacyPopover
                content={<FormattedMessage id="photo-shoot.photo-linked" />}
                placement="top"
                showOnClick={false}
              >
                <LinkIconBox>
                  <LinkIcon />
                </LinkIconBox>
              </LegacyPopover>
            </LinkIconContainer>
          )}
        </>
      )}

      {error ? (
        <PhotoError>
          <PhotoErrorMessage>
            <ErrorIcon />
            <FormattedMessage id="photo-shoot.photo-loading-error" />
          </PhotoErrorMessage>
          <PhotoErrorName>{image.name}</PhotoErrorName>
        </PhotoError>
      ) : null}
    </Root>
  );
};
